import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import { ARCHETYPES, type ArchetypeDefinition } from '../../lib/archetypes';

interface MoodDialProps {
  archetypes?: readonly ArchetypeDefinition[];
  selected: ArchetypeId | null;
  onSelect: (id: ArchetypeId) => void;
  size?: number;
}

const DEG = Math.PI / 180;

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg - 90) * DEG;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** SVG donut-arc path between two angles (degrees), going clockwise. */
function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  const o1 = polar(cx, cy, rOuter, start);
  const o2 = polar(cx, cy, rOuter, end);
  const i1 = polar(cx, cy, rInner, end);
  const i2 = polar(cx, cy, rInner, start);
  const large = end - start > 180 ? 1 : 0;
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${o2.x} ${o2.y}`,
    `L ${i1.x} ${i1.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${i2.x} ${i2.y}`,
    'Z',
  ].join(' ');
}

/** Strip "The "/"Die " article so legend entries stay short. */
function shortLabel(title: string): string {
  return title.replace(/^(The|Die) /i, '');
}

/**
 * Circular 5-segment selector. Each segment shows only an archetype glyph —
 * names live in a legend strip below the dial where they're never clipped or
 * overlapped by the central overlay. The legend doubles as a flat list of
 * selection buttons for keyboard and discoverability.
 *
 * Selecting (segment OR legend entry) bubbles `onSelect`. The parent decides
 * what to do (persist, re-theme, change daily command, …).
 */
export function MoodDial({
  archetypes = ARCHETYPES,
  selected,
  onSelect,
  size = 280,
}: MoodDialProps) {
  const { strings } = useStrings();
  const haptic = useHaptic();

  const segmentCount = archetypes.length;
  const segmentDeg = 360 / segmentCount;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 2;
  const rInner = size * 0.32;
  const glyphR = (rOuter + rInner) / 2;

  const segments = useMemo(
    () =>
      archetypes.map((a, i) => {
        const start = i * segmentDeg - 90;
        const end = start + segmentDeg;
        const mid = start + segmentDeg / 2;
        return {
          archetype: a,
          path: arcPath(cx, cy, rOuter, rInner, start + 90, end + 90),
          glyphPos: polar(cx, cy, glyphR, mid + 90),
        };
      }),
    [archetypes, cx, cy, glyphR, rInner, rOuter, segmentDeg],
  );

  const selectedStrings = selected ? strings.archetypes[selected] : null;

  const handleSelect = (id: ArchetypeId) => {
    haptic.tap();
    onSelect(id);
  };

  // Per-entry info popover. Click-outside closes; Escape closes.
  const [infoOpen, setInfoOpen] = useState<ArchetypeId | null>(null);
  const legendRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!infoOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!legendRef.current) return;
      if (!legendRef.current.contains(e.target as Node)) setInfoOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfoOpen(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  return (
    <div className="mx-auto flex flex-col items-center gap-3">
      <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
        {strings.yourSpace.moodDial.eyebrow}
      </p>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="radiogroup"
          aria-label={strings.yourSpace.moodDial.instruction}
          className="touch-manipulation overflow-visible"
        >
          <defs>
            <filter id="dial-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <circle
            cx={cx}
            cy={cy}
            r={rOuter}
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth={1}
          />

          {segments.map(({ archetype, path, glyphPos }, i) => {
            const isSelected = selected === archetype.id;
            const archetypeStrings = strings.archetypes[archetype.id];
            return (
              <motion.g
                key={archetype.id}
                role="radio"
                aria-checked={isSelected}
                aria-label={archetypeStrings.title}
                tabIndex={0}
                onClick={() => handleSelect(archetype.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(archetype.id);
                  }
                }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05, duration: 0.45, ease: 'easeOut' }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  cursor: 'pointer',
                  outline: 'none',
                  color: `hsl(${archetype.color.primary})`,
                }}
              >
                <motion.path
                  d={path}
                  animate={{
                    fill: isSelected
                      ? `hsl(${archetype.color.primary} / 0.55)`
                      : `hsl(${archetype.color.primary} / 0.14)`,
                    stroke: isSelected
                      ? `hsl(${archetype.color.secondary})`
                      : 'hsl(var(--border))',
                    strokeWidth: isSelected ? 1.5 : 0.75,
                  }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  style={isSelected ? { filter: 'url(#dial-glow)' } : undefined}
                />
                <g
                  transform={`translate(${glyphPos.x - 14} ${glyphPos.y - 14})`}
                  pointerEvents="none"
                  style={{
                    // Always use the brighter secondary color so dark archetypes
                    // (Enchantress midnight, Priestess violet) stay legible
                    // against the faint segment background.
                    color: `hsl(${archetype.color.secondary})`,
                  }}
                >
                  <archetype.Icon width={28} height={28} />
                </g>
              </motion.g>
            );
          })}

          <circle
            cx={cx}
            cy={cy}
            r={rInner - 4}
            fill="hsl(var(--background))"
            stroke="hsl(var(--border))"
            strokeWidth={0.75}
          />
        </svg>

        {/* Centered overlay — lives inside the dial's inner hole. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="max-w-[130px] text-center">
            {selectedStrings ? (
              <motion.div
                key={selected ?? 'empty'}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <p className="font-serif text-lg leading-tight text-foreground">
                  {selectedStrings.title}
                </p>
                <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                  {selectedStrings.tagline}
                </p>
              </motion.div>
            ) : (
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {strings.yourSpace.moodDial.emptyState}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Legend — flat list of selectable buttons + per-entry info popover.
          Names are always readable here, never clipped by the dial geometry,
          and the popover gives the user a quick "what is this archetype?"
          glance without having to commit to a selection. */}
      <ul
        ref={legendRef}
        className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-2"
      >
        {archetypes.map((a) => {
          const isSelected = selected === a.id;
          const archetypeStrings = strings.archetypes[a.id];
          const isInfoOpen = infoOpen === a.id;
          return (
            <li key={a.id} className="relative">
              <div
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] transition-colors ${
                  isSelected
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleSelect(a.id)}
                  aria-pressed={isSelected}
                  className="inline-flex items-center gap-1.5"
                >
                  <span
                    aria-hidden
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: `hsl(${a.color.primary})`,
                      boxShadow: isSelected
                        ? `0 0 8px hsl(${a.color.primary} / 0.7)`
                        : undefined,
                    }}
                  />
                  {shortLabel(archetypeStrings.title)}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoOpen((prev) => (prev === a.id ? null : a.id));
                  }}
                  aria-label={`About ${archetypeStrings.title}`}
                  aria-expanded={isInfoOpen}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground/60 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60"
                >
                  <Info className="h-3 w-3" />
                </button>
              </div>

              <AnimatePresence>
                {isInfoOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.96 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    role="dialog"
                    aria-label={archetypeStrings.title}
                    className="absolute left-1/2 top-full z-30 mt-2 w-60 -translate-x-1/2 rounded-xl border border-border bg-card/95 p-3 text-left shadow-xl backdrop-blur-sm"
                    style={{
                      borderColor: `hsl(${a.color.primary} / 0.5)`,
                      boxShadow: `0 10px 30px hsl(${a.color.primary} / 0.25)`,
                    }}
                  >
                    <p
                      className="font-serif text-base leading-tight"
                      style={{ color: `hsl(${a.color.primary})` }}
                    >
                      {archetypeStrings.title}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {archetypeStrings.tagline}
                    </p>
                    <p className="mt-2 text-xs leading-snug text-foreground/85">
                      {archetypeStrings.description}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
